
import { useEffect, useMemo, useState } from "react";
import { HeaderApprovedBuys } from "./approveBuysTableComponents/HeaderApprovedBuys";
import { SummaryApprovedBuys } from "./approveBuysTableComponents/SummaryApprovedBuys";
import { ReviewQueueApprovedBuys } from "./approveBuysTableComponents/ReviewQueueApprovedBuys";
import { useDispatch, useSelector } from "react-redux";
import {
    DemandPlanningLoadingKeys,
    getApprovedBuysTableData,
    saveForecastDemandProductsDraftedData
} from "../../slices/demandPlanningSlice";
import { LoaderSpinner } from "../LoaderSpinner";
import { arrayHasItems } from "../../helpers/common";

const EMPTY_DRAFT_QUEUE_DELTA = {
    hiddenCandidateIds: [],
    addedDraftedPos: [],
    restoredCandidateLogs: [],
    draftedPoUpdates: {},
    hiddenDraftedPoIds: [],
};

const ApprovedBuysTable = ({
    selectedForecastDemandPlanning,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
    marginFromSidebar,
}) => {

    const dispatch = useDispatch();
    const { 
        approvedBuysTableData, 
        loadingKeys 
    } = useSelector((state) => state.demandPlanning);

    const [summaryIsOpened, setSummaryIsOpened] = useState(true);
    const [reviewQueueIsOpened, setReviewQueueIsOpened] = useState(true);
    const [hasLoadedApprovedBuys, setHasLoadedApprovedBuys] = useState(false);

    const [draftQueueDelta, setDraftQueueDelta] = useState(EMPTY_DRAFT_QUEUE_DELTA);

    const approvedLogs = useMemo(() => {
        const rows = approvedBuysTableData?.approvalProducts;
        return arrayHasItems(rows) ? rows : [];
    }, [approvedBuysTableData]);
    const draftedLogs = useMemo(() => {
        const rows = approvedBuysTableData?.draftedLogs;
        const serverRows = arrayHasItems(rows) ? rows : [];
        const hiddenIds = new Set(draftQueueDelta.hiddenCandidateIds);
        const fromServer = serverRows.filter((row) => !hiddenIds.has(row.id));
        const serverIds = new Set(fromServer.map((row) => row.id));
        const restored = (draftQueueDelta.restoredCandidateLogs || []).filter(
            (row) => !hiddenIds.has(row.id) && !serverIds.has(row.id)
        );
        return [...fromServer, ...restored];
    }, [approvedBuysTableData, draftQueueDelta.hiddenCandidateIds, draftQueueDelta.restoredCandidateLogs]);
    const draftedPos = useMemo(() => {
        const rows = approvedBuysTableData?.draftedPos;
        const serverRows = arrayHasItems(rows) ? rows : [];
        const hiddenIds = new Set(draftQueueDelta.hiddenDraftedPoIds);
        const updates = draftQueueDelta.draftedPoUpdates || {};
        const fromServer = serverRows
            .filter((row) => !hiddenIds.has(row.id))
            .map((row) => (updates[row.id] ? { ...row, ...updates[row.id] } : row));
        const added = (draftQueueDelta.addedDraftedPos || []).filter(
            (row) => !fromServer.some((existing) => existing.id === row.id)
        );
        return [...fromServer, ...added];
    }, [approvedBuysTableData, draftQueueDelta]);

    const replenishmentCashByForecastProductId = useMemo(() => {
        const m = {};
        for (const row of approvedLogs) {
            const id = Number(row?.id);
            if (Number.isFinite(id) && id > 0) {
                m[id] = Number(row.replenishmentCashRequired) || 0;
            }
        }
        return m;
    }, [approvedLogs]);

    const approvedBuyRows = approvedLogs.length;
    const draftedPOs = draftedLogs.length;
    const approvalLogEntries = approvedLogs.length;
    const approvedBuysTableLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getApprovedBuysTableData
    );
    const saveDraftedDataLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.saveForecastDemandProductsDraftedData
    );

    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
        selectedForecastDemandPlanning?.forecast_year ??
        selectedForecastDemandPlanning?.forecastYear;

    useEffect(() => {
        if (!demandPlanningId) return;

        setHasLoadedApprovedBuys(false);
        setDraftQueueDelta(EMPTY_DRAFT_QUEUE_DELTA);
        dispatch(
            getApprovedBuysTableData({
                demandPlanningId,
                demandPlanningYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            })
        );
    }, [dispatch, demandPlanningId, demandPlanningYear, searchTerm, selectedCategoryId, selectedSubcategoryId, selectedStatusId]);

    useEffect(() => {
        if (!approvedBuysTableLoading && approvedBuysTableData) {
            setHasLoadedApprovedBuys(true);
        }
    }, [approvedBuysTableLoading, approvedBuysTableData]);

    const buildDraftedLogFromPo = (draftedPo) => ({
        id: draftedPo.id,
        groupName: draftedPo.groupName,
        supplierName: draftedPo.supplierName || '',
        variants: draftedPo.variants || [],
        replenishmentCashRequired: draftedPo.cashRequired ?? 0,
    });

    const buildDraftedPoFromLog = (draftedLog, apiResult, allowSplit) => ({
        id: draftedLog.id,
        groupName: draftedLog.groupName,
        supplierName: draftedLog.supplierName || '',
        variants: draftedLog.variants || [],
        cashRequired: draftedLog.replenishmentCashRequired ?? 0,
        splittedCount: 0,
        exceptionForecastDemandProductIds: [],
        purchaseOrderId: apiResult?.purchaseOrderId ?? null,
        poNumber: apiResult?.poNumber ?? null,
        splitPurchaseOrderId: apiResult?.splitPurchaseOrderId ?? null,
        splitPoNumber: apiResult?.splitPoNumber ?? null,
        allowSplit,
    });

    const collectForecastDemandProductIds = (variants) =>
        arrayHasItems(variants)
            ? variants
                .map((variant) => Number.parseInt(String(variant?.id ?? ''), 10))
                .filter((id) => Number.isFinite(id) && id > 0)
            : [];

    const setGroupToDrafted = async (draftedLog, options = {}) => {
        if (!demandPlanningId || !demandPlanningYear || !draftedLog) return;
        const forecastDemandProductIds = collectForecastDemandProductIds(draftedLog.variants);
        if (!arrayHasItems(forecastDemandProductIds)) return;
        const allowSplit = Boolean(options?.shouldOpenSplit);
        try {
            const result = await dispatch(
                saveForecastDemandProductsDraftedData({
                    demandPlanningId,
                    demandPlanningYear,
                    forecastDemandProductIds,
                    draftedData: {
                        isDrafted: true,
                        id: draftedLog.id,
                        groupName: draftedLog.groupName,
                        supplierName: draftedLog.supplierName || '',
                        variants: draftedLog.variants || [],
                        cashRequired: draftedLog.replenishmentCashRequired ?? 0,
                        splittedCount: 0,
                        exceptionForecastDemandProductIds: [],
                        allowSplit,
                    },
                })
            ).unwrap();

            setDraftQueueDelta((prev) => ({
                hiddenCandidateIds: [...prev.hiddenCandidateIds, draftedLog.id],
                addedDraftedPos: [
                    ...prev.addedDraftedPos,
                    buildDraftedPoFromLog(draftedLog, result, allowSplit),
                ],
                restoredCandidateLogs: prev.restoredCandidateLogs.filter(
                    (log) => log.id !== draftedLog.id
                ),
                draftedPoUpdates: prev.draftedPoUpdates,
                hiddenDraftedPoIds: prev.hiddenDraftedPoIds,
            }));

            return {
                ...options,
                purchaseOrderId: result?.purchaseOrderId ?? null,
                poNumber: result?.poNumber ?? null,
            };
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const setGroupBackToQueue = async (draftedPo) => {
        if (!demandPlanningId || !demandPlanningYear || !draftedPo) return;
        const forecastDemandProductIds = collectForecastDemandProductIds(draftedPo.variants);
        if (!arrayHasItems(forecastDemandProductIds)) return;
        try {
            await dispatch(
                saveForecastDemandProductsDraftedData({
                    demandPlanningId,
                    demandPlanningYear,
                    forecastDemandProductIds,
                    draftedData: null,
                })
            ).unwrap();

            setDraftQueueDelta((prev) => ({
                hiddenCandidateIds: prev.hiddenCandidateIds.filter((id) => id !== draftedPo.id),
                addedDraftedPos: prev.addedDraftedPos.filter((po) => po.id !== draftedPo.id),
                restoredCandidateLogs: [
                    ...prev.restoredCandidateLogs.filter((log) => log.id !== draftedPo.id),
                    buildDraftedLogFromPo(draftedPo),
                ],
                draftedPoUpdates: Object.fromEntries(
                    Object.entries(prev.draftedPoUpdates).filter(([id]) => id !== draftedPo.id)
                ),
                hiddenDraftedPoIds: prev.hiddenDraftedPoIds.includes(draftedPo.id)
                    ? prev.hiddenDraftedPoIds
                    : [...prev.hiddenDraftedPoIds, draftedPo.id],
            }));
        } catch (error) {
            console.error(error);
        }
    };

    const persistDraftedSplit = async (draftedPo, exceptionForecastDemandProductIds) => {
        if (!demandPlanningId || !demandPlanningYear || !draftedPo) return;
        const forecastDemandProductIds = collectForecastDemandProductIds(draftedPo.variants);
        if (!arrayHasItems(forecastDemandProductIds)) return;
        try {
            const result = await dispatch(
                saveForecastDemandProductsDraftedData({
                    demandPlanningId,
                    demandPlanningYear,
                    forecastDemandProductIds,
                    draftedData: {
                        isDrafted: true,
                        id: draftedPo.id,
                        groupName: draftedPo.groupName,
                        supplierName: draftedPo.supplierName || '',
                        variants: draftedPo.variants || [],
                        cashRequired: draftedPo.cashRequired ?? 0,
                        splittedCount: exceptionForecastDemandProductIds.length,
                        exceptionForecastDemandProductIds,
                        purchaseOrderId: draftedPo.purchaseOrderId || null,
                        poNumber: draftedPo.poNumber || null,
                        splitPurchaseOrderId: draftedPo.splitPurchaseOrderId || null,
                        splitPoNumber: draftedPo.splitPoNumber || null,
                        allowSplit: draftedPo.allowSplit !== false,
                    },
                })
            ).unwrap();

            const splitPatch = {
                splittedCount: exceptionForecastDemandProductIds.length,
                exceptionForecastDemandProductIds,
                splitPurchaseOrderId: result?.splitPurchaseOrderId ?? null,
                splitPoNumber: result?.splitPoNumber ?? null,
            };

            setDraftQueueDelta((prev) => {
                const isLocallyAdded = prev.addedDraftedPos.some((po) => po.id === draftedPo.id);
                if (isLocallyAdded) {
                    return {
                        ...prev,
                        addedDraftedPos: prev.addedDraftedPos.map((po) =>
                            po.id === draftedPo.id ? { ...po, ...splitPatch } : po
                        ),
                    };
                }
                return {
                    ...prev,
                    draftedPoUpdates: {
                        ...prev.draftedPoUpdates,
                        [draftedPo.id]: {
                            ...(prev.draftedPoUpdates[draftedPo.id] || {}),
                            ...splitPatch,
                        },
                    },
                };
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    if (approvedBuysTableLoading && !hasLoadedApprovedBuys) {
        return (
            <section className="flex-1 px-4 py-4 lg:px-6">
                <div className="flex justify-center items-center h-full mt-8">
                    <LoaderSpinner
                        width="80"
                        height="80"
                        border="5"
                        colorSpinner="gray"
                    />
                </div>
            </section>
        );
    }

    return (
        <section className="flex-1 py-2">
            <div className="mb-4 rounded-lg border border-border bg-white p-4 shadow-sm">
                <HeaderApprovedBuys
                    summaryIsOpened={summaryIsOpened}
                    setSummaryIsOpened={setSummaryIsOpened}
                    reviewQueueIsOpened={reviewQueueIsOpened}
                    setReviewQueueIsOpened={setReviewQueueIsOpened}
                />
                {summaryIsOpened && (
                    <SummaryApprovedBuys
                        approvedBuyRows={approvedBuyRows}
                        draftedPOs={draftedPOs}
                        approvalLogEntries={approvalLogEntries}
                    />
                )}
                {reviewQueueIsOpened && (
                    <ReviewQueueApprovedBuys
                        draftedLogs={draftedLogs}
                        draftedPos={draftedPos}
                        marginFromSidebar={marginFromSidebar}
                        approvedLogs={approvedLogs}
                        onDraftGroup={setGroupToDrafted}
                        onReturnToQueue={setGroupBackToQueue}
                        isPersistingSplit={saveDraftedDataLoading}
                        replenishmentCashByForecastProductId={replenishmentCashByForecastProductId}
                        onPersistDraftedSplit={persistDraftedSplit}
                    />
                )}
            </div>
        </section>
    )
};

export default ApprovedBuysTable;